import { Injectable, inject } from '@angular/core';
import { ChartConstants } from '@modules/apc/apc/data/chart-modules';
import { CustomRendererService } from '@shared/services/custom-renderer.service';

declare let Bokeh_v0123: any;

const Defaults = {
  Chart: {
    FullWidth: 1160,
    Height: 380,
    BackgroundFillColor: '#FFF',
    BorderFillColor: '#FFF',
    AxisLabelTextColor: '#444444',
  },
  Legend: {
    BorderWidth: 0,
    BackgroundFillAlpha: 0.85,
  },
  LabelTextColor: '#444444',
  GridLineColor: '#a2a2a2',
  BorderColor: '#a2a2a2',
};

@Injectable({
  providedIn: 'root',
})
export class PPDCrossValidationService {
  private readonly customRendererService = inject(CustomRendererService);

  destroyChart(elementId: string): void {
    this.customRendererService.emptyAndCleanupBokeh(elementId);
  }

  renderPPDCrossValidation(
    metabolicPlot6: Record<string, any>,
    elementId?: string,
    scaleText = 1,
    renderForPdf = false
  ): void {
    if (!metabolicPlot6) {
      return;
    }
    const testDataTab = Array.isArray(metabolicPlot6) && metabolicPlot6.length ? metabolicPlot6[0] : metabolicPlot6;
    if (!testDataTab) {
      return;
    }

    this.customRendererService.emptyAndCleanupBokeh(elementId || ChartConstants.TEST_DATA.ELEMENT_ID);
    this.generatePPDCrossValidation(
      testDataTab,
      elementId || ChartConstants.TEST_DATA.ELEMENT_ID,
      scaleText,
      renderForPdf
    );
  }

  private generatePPDCrossValidation(
    testDataTab: Record<string, any>,
    elementId: string,
    scaleText = 1,
    renderForPdf = false
  ): void {
    return this.generatePPDChart(elementId, testDataTab.cp19.calculated_values, true, scaleText, renderForPdf);
  }

  private generatePPDChart(
    elementId: any,
    optimizedValues: any,
    showLabel: any,
    scaleText = 1,
    renderForPdf = false
  ): void {
    let data = optimizedValues;
    let fontSizeValue = 7 * scaleText;

    let VLsource = new Bokeh_v0123.ColumnDataSource({
      data: {
        x: [data.VL_x],
        y: [data.VL_y],
        radius: [5],
        color: ['#C10020'],
      },
    });

    let VOsource = new Bokeh_v0123.ColumnDataSource({
      data: {
        x: [data.VO_x],
        y: [data.VO_y],
        radius: [5],
        color: ['#00538A'],
      },
    });

    let ATsource = new Bokeh_v0123.ColumnDataSource({
      data: {
        x: [data.AT_x],
        y: [data.AT_y],
        radius: [5],
        color: ['#007D34'],
      },
    });

    let xValues: any[] = [data.VL_x, data.VO_x, data.AT_x];
    let yValues: any[] = [data.VL_y, data.VO_y, data.AT_y];
    let errorLow: number = 0.95;
    let errorHigh: number = 1.05;
    let globalMin: number = 0.8 * Math.min(...xValues, ...yValues);
    let globalMax: number = 1.2 * Math.max(...xValues, ...yValues);

    let plt = Bokeh_v0123.Plotting;
    let figure_config = {
      tools: 'pan, box_zoom, wheel_zoom, reset,save',
      width: Defaults.Chart.FullWidth,
      height: Defaults.Chart.Height,
      toolbar_location: 'above',
      background_fill_color: Defaults.Chart.BackgroundFillColor,
      border_fill_color: Defaults.Chart.BorderFillColor,
      outline_line_color: Defaults.BorderColor,
      sizing_mode: 'scale_width',
    };
    if (renderForPdf) {
      (figure_config as any).toolbar_location = null;
    }
    let p = plt.figure(figure_config);

    let crossLineSource = new Bokeh_v0123.ColumnDataSource({
      data: {
        x: [globalMin, globalMax],
        y: [globalMin, globalMax],
      },
    });

    let vLErrorLineSource = new Bokeh_v0123.ColumnDataSource({
      data: {
        x: [data.VL_x, data.VL_x],
        y: [data.VL_y_lower || data.VL_y * errorLow, data.VL_y_upper || data.VL_y * errorHigh],
      },
    });

    let vOErrorLineSource = new Bokeh_v0123.ColumnDataSource({
      data: {
        x: [data.VO_x, data.VO_x],
        y: [data.VO_y_lower || data.VO_y * errorLow, data.VO_y_upper || data.VO_y * errorHigh],
      },
    });

    let aTErrorLineSource = new Bokeh_v0123.ColumnDataSource({
      data: {
        x: [data.AT_x, data.AT_x],
        y: [data.AT_y_lower || data.AT_y * errorLow, data.AT_y_upper || data.AT_y * errorHigh],
      },
    });

    if (showLabel) {
      p.yaxis.axis_label = 'Measured value in Power [Watt]';
      p.xaxis.axis_label = 'Calculated value in Power [Watt]';
      p.title.text = 'Cross validation';
      p.title.text_color = Defaults.Chart.AxisLabelTextColor;
      p.title.text_font_size = `${fontSizeValue + 1}pt`;
      p.yaxis.axis_label_text_font_size = `${fontSizeValue}pt`;
      p.yaxis.major_label_text_font_size = `${fontSizeValue}pt`;
      p.yaxis.axis_label_text_font_style = 'normal';
      p.yaxis.axis_label_text_color = Defaults.Chart.AxisLabelTextColor;
      p.yaxis.major_label_text_color = Defaults.Chart.AxisLabelTextColor;
      p.xaxis.axis_label_text_font_size = `${fontSizeValue}pt`;
      p.xaxis.axis_label_text_font_style = 'normal';
      p.xaxis.major_label_text_font_size = `${fontSizeValue}pt`;
      p.xaxis.axis_label_text_color = Defaults.Chart.AxisLabelTextColor;
      p.xaxis.major_label_text_color = Defaults.Chart.AxisLabelTextColor;
    } else {
      p.xaxis.visible = false;
      p.yaxis.visible = false;
    }

    // B. Real line
    p.line(
      { field: 'x' },
      { field: 'y' },
      {
        source: crossLineSource,
        line_color: '#3c3c3d',
        line_width: 1,
      }
    );

    // C. Error lines
    p.line(
      { field: 'x' },
      { field: 'y' },
      {
        source: vLErrorLineSource,
        line_color: '#C10020',
        line_width: 1,
      }
    );
    p.line(
      { field: 'x' },
      { field: 'y' },
      {
        source: vOErrorLineSource,
        line_color: '#00538A',
        line_width: 1,
      }
    );
    p.line(
      { field: 'x' },
      { field: 'y' },
      {
        source: aTErrorLineSource,
        line_color: '#007D34',
        line_width: 1,
      }
    );

    // Add 3 circles
    // VOcircles
    p.circle(
      {
        field: 'x',
      },
      {
        field: 'y',
      },
      {
        source: VOsource,
        size: fontSizeValue,
        fill_color: VOsource.data.color,
        line_color: null,
        legend: 'VO2max',
      }
    );
    // VLcircles
    p.circle(
      {
        field: 'x',
      },
      {
        field: 'y',
      },
      {
        source: VLsource,
        size: fontSizeValue,
        fill_color: VLsource.data.color,
        line_color: null,
        legend: 'VLamax',
      }
    );
    // ATcircles
    p.circle(
      {
        field: 'x',
      },
      {
        field: 'y',
      },
      {
        source: ATsource,
        size: fontSizeValue,
        fill_color: ATsource.data.color,
        line_color: null,
        legend: 'Critical Power',
      }
    );

    p._legend.location = 'top_left';
    p._legend.background_fill_color = Defaults.Chart.BackgroundFillColor;
    p._legend.border_line_width = Defaults.Legend.BorderWidth;
    p._legend.background_fill_alpha = Defaults.Legend.BackgroundFillAlpha;
    p._legend.label_text_color = Defaults.LabelTextColor;
    p._legend.label_text_font_size = `${fontSizeValue}pt`;
    p._legend.margin = fontSizeValue;
    p._legend.padding = fontSizeValue;
    p._legend.glyph_height = fontSizeValue * 2;
    p._legend.glyph_width = fontSizeValue * 2;
    p._legend.label_height = fontSizeValue;
    p._legend.label_width = fontSizeValue;
    p.xgrid.grid_line_color = Defaults.GridLineColor;
    p.ygrid.grid_line_color = Defaults.GridLineColor;

    plt.show(p, elementId);
  }
}
